import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {useNavigate, useParams} from "react-router-dom";
import Switch from '../components/Switch'
import EmployeeList from '../components/EmployeeList'
import {Colors} from '../constants/colors'
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import {
  CreatePayArticleTypeModel,
  BankAccountModel,
  CreditorConnectionModel,
  EmployerLogoModel,
  PayArticleTypeModel,
  PayoutSettings,
  PayrollCodeConfigurationModel,
  PayrollSystemConfigurationModel,
  UpdatePayArticleTypeModel,
  UpdateEmployerModel,
} from '../redux/types'
import ConfirmModal from '../components/ConfirmModal'
import BankAccountSelectorModal from '../components/BankAccount/BankAccountSelectorModal'
import EditPayoutSettingsModal from '../components/EditPayoutSettingsModal';
import EditEmployerInfoModal from '../components/EditEmployerModal';
import {formatDateTimeString} from '../utils/dateHelper'
import useEmployerActions from '../hooks/useEmployerActions';
import {useAppSelector} from '../redux/hooks';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import Divider from '../components/Divider';
import PayScheduleList from '../components/PaySchedule/PayScheduleList';
import CreditorConnectionList from '../components/CreditorConnections/EmployerCreditorList';
import AddCreditorConnectionModal from '../components/CreditorConnections/AddEmployerCreditorModal';
import {faArrowLeft, faPen} from "@fortawesome/free-solid-svg-icons";
import NavLink from '../components/NavLink';
import {titleMarginBottom} from '../constants/layout';
import EmployerLogoList from "../components/EmployerLogo/EmployerLogoList";
import UploadLogoModal from "../components/EmployerLogo/UploadLogoModal";
import EmployerBookkeepingConfiguration from "../components/EmployerBookkeepingConfiguration/EmployerBookkeepingConfiguration";
import Link from '../components/Link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SilenceCreditSpaceWarningModal from '../components/CreditSpace/SilenceCreditSpaceWarningModal';
import EditPayrollSystemConfigurationModal from '../components/EditPayrollSystemConfigurationModal';
import PayrollCodeList from '../components/PayrollCode/PayrollCodeList';
import AddPayrollCodeModal from '../components/PayrollCode/AddPayrollCodeModal';
import PayArticleTypeList from '../components/PayArticleType/PayArticleTypeList';
import {formatMoney} from '../utils/moneyHelper';
import EditPayArticleTypeModal from '../components/PayArticleType/EditPayArticleTypeModal';
import {Alert} from 'react-bootstrap';
import StateBadge from '../components/StateBadge';

const EmployerDetails = () => {
  const styles = useStyles()
  const params = useParams<{id: string}>()
  const navigate = useNavigate();

  const [confirmSetBankAccountModalVisible, setConfirmSetBankAccountModalVisible] = useState(false)
  const [bankAccountSelectorModalVisible, setBankAccountSelectorModalVisible] = useState(false)
  const [addEmployerCreditorModalVisible, setAddEmployerCreditorModalVisible] = useState(false)
  const [confirmDeleteEmployerCreditorModalVisible, setConfirmDeleteEmployerCreditorModalVisible] = useState(false)
  const [confirmDeletePayrollCodeModalVisible, setConfirmDeletePayrollCodeModalVisible] = useState(false)
  const [confirmDeletePayArticleTypeModalVisible, setConfirmDeletePayArticleTypeModalVisible] = useState(false)
  const [payoutSettingsEditModalVisible, setPayoutSettingsEditModalVisible] = useState(false)
  const [employerEditModalVisible, setEmployerEditModalVisible] = useState(false)
  const [payrollSystemConfigurationEditModalVisible, setPayrollSystemConfigurationEditModalVisible] = useState(false)
  const [uploadLogoModalVisible, setUploadLogoModalVisible] = useState(false)
  const [confirmDeleteLogoModalVisible, setConfirmDeleteLogoModalVisible] = useState(false)
  const [silenceCreditSpaceWarningModalVisible, setSilenceCreditSpaceWarningModalVisible] = useState(false)
  const [addPayrollCodeModalVisible, setAddPayrollCodeModalVisible] = useState(false)
  const [payArticleTypeModalVisible, setPayArticleTypeModalVisible] = useState(false)
  const [payArticleTypeModalError, setPayArticleTypeModalError] = useState<string | null>(null)

  const [employerCreditorDeleteCandidate, setEmployerCreditorDeleteCandidate] = useState<CreditorConnectionModel | null>(null)
  const [payrollCodeDeleteCandidate, setPayrollCodeDeleteCandidate] = useState<PayrollCodeConfigurationModel | null>(null)
  const [payArticleTypeDeleteCandidate, setPayArticleTypeDeleteCandidate] = useState<PayArticleTypeModel | null>(null)
  const [payArticleTypeEditCandidate, setPayArticleTypeEditCandidate] = useState<PayArticleTypeModel | null>(null)
  const [bankAccountCandidate, setBankAccountCandidate] = useState<{id: string, number: string} | null>(null)
  const [employerLogoDeleteCandidate, setEmployerLogoDeleteCandidate] = useState<EmployerLogoModel | null>(null)

  const loading = useAppSelector(state => state.employer.loading)
  const payArticleTypes = useAppSelector(state => state.employer.payArticleTypes)
  const payArticleTypesLoading = useAppSelector(state => state.employer.payArticleTypesLoading)
  const payArticleTypesError = useAppSelector(state => state.employer.payArticleTypesError)
  const employer = useAppSelector(state => state.employer.employerDetails)

  const {
    getEmployerDetails,
    getPayArticleTypes,
    getPayArticleType,
    setBankAccount,
    updatePayoutSettings,
    updateEmployerInfo,
    updatePayrollSystemConfiguration,
    addEmployerCreditor,
    deleteEmployerCreditor,
    uploadEmployerLogo,
    deleteEmployerLogo,
    silenceCreditSpaceWarning,
    createPayArticleType,
    updatePayArticleType,
    deletePayArticleType
  } = useEmployerActions()

  const inherited = '(inherited)'

  useEffect(() => {
    if (params.id && (employer?.id == null || employer.id !== params.id)) {
      getEmployerDetails(params.id)
    }

    if (params.id) {
      getPayArticleTypes(params.id)
    }
  }, [params.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteEmployerCreditor = (creditor: CreditorConnectionModel) => {
    setEmployerCreditorDeleteCandidate(creditor)
    setConfirmDeleteEmployerCreditorModalVisible(true)
  }

  const handleConfirmDeleteEmployerCreditor = () => {
    deleteEmployerCreditor(employer?.id as string, employerCreditorDeleteCandidate?.id || '')
    setEmployerCreditorDeleteCandidate(null)
    setConfirmDeleteEmployerCreditorModalVisible(false)
  }

  const handleAddPayrollCode = (payrollCode: PayrollCodeConfigurationModel) => {
    let config = {...employer!.payrollSystemConfiguration, payrollCodes: [...employer!.payrollSystemConfiguration.payrollCodes]}
    config.payrollCodes.push(payrollCode)
    updatePayrollSystemConfiguration(employer?.id as string, config)
    setAddPayrollCodeModalVisible(false)
  }

  const handleDeletePayrollCode = (payrollCode: PayrollCodeConfigurationModel) => {
    setPayrollCodeDeleteCandidate(payrollCode)
    setConfirmDeletePayrollCodeModalVisible(true)
  }

  const handleAddPayArticleTypeClick = () => {
    setPayArticleTypeEditCandidate(null)
    setPayArticleTypeModalError(null)
    setPayArticleTypeModalVisible(true)
  }

  const handleEditPayArticleType = (payArticleType: PayArticleTypeModel) => {
    if (!employer?.id) {
      return
    }

    setPayArticleTypeModalError(null)

    getPayArticleType(employer.id, payArticleType.id)
      .then((response) => {
        setPayArticleTypeEditCandidate(response)
        setPayArticleTypeModalVisible(true)
      })
      .catch(() => {})
  }

  const handleDeletePayArticleType = (payArticleType: PayArticleTypeModel) => {
    setPayArticleTypeDeleteCandidate(payArticleType)
    setConfirmDeletePayArticleTypeModalVisible(true)
  }

  const handleConfirmDeletePayArticleType = () => {
    if (!employer?.id || !payArticleTypeDeleteCandidate?.id) {
      return
    }

    deletePayArticleType(employer.id, payArticleTypeDeleteCandidate.id)
      .finally(() => {
        setPayArticleTypeDeleteCandidate(null)
        setConfirmDeletePayArticleTypeModalVisible(false)
      })
  }

  const handlePayArticleTypeModalClose = () => {
    setPayArticleTypeModalError(null)
    setPayArticleTypeEditCandidate(null)
    setPayArticleTypeModalVisible(false)
  }

  const handleSavePayArticleType = (model: CreatePayArticleTypeModel | UpdatePayArticleTypeModel) => {
    if (!employer?.id) {
      return
    }

    const action = payArticleTypeEditCandidate
      ? updatePayArticleType(employer.id, payArticleTypeEditCandidate.id, model as UpdatePayArticleTypeModel)
      : createPayArticleType(employer.id, model as CreatePayArticleTypeModel)

    action
      .then(() => {
        handlePayArticleTypeModalClose()
      })
      .catch((error) => {
        setPayArticleTypeModalError(error?.toString() ?? 'Failed to save pay article type')
      })
  }

  const handleConfirmDeletePayrollCode = () => {
    let config = {...employer!.payrollSystemConfiguration, payrollCodes: [...employer!.payrollSystemConfiguration.payrollCodes]}
    const index = config.payrollCodes.indexOf(payrollCodeDeleteCandidate!);
    config.payrollCodes.splice(index, 1);

    updatePayrollSystemConfiguration(employer?.id as string, config)
    setPayrollCodeDeleteCandidate(null)
    setConfirmDeletePayrollCodeModalVisible(false)
  }

  const handleAddEmployerCreditor = (creditor: CreditorConnectionModel) => {
    addEmployerCreditor(employer?.id as string, creditor)
    setAddEmployerCreditorModalVisible(false)
  }

  const handleBankAccountSelected = (bankAccount: BankAccountModel) => {
    setBankAccountCandidate({id: bankAccount.bankAccountId, number: bankAccount.bban})
    setConfirmSetBankAccountModalVisible(true)
    setBankAccountSelectorModalVisible(false)
  }

  const handleSetBankAccountConfirmed = () => {
    if (bankAccountCandidate)
      setBankAccount(employer?.id as string, bankAccountCandidate.id, bankAccountCandidate.number)

    setConfirmSetBankAccountModalVisible(false)
  }

  const handlePayoutSettingsChange = (payoutSettings: PayoutSettings) => {
    updatePayoutSettings(employer?.id as string, payoutSettings)
    setPayoutSettingsEditModalVisible(false)
  }

  const handleEmployerInfoChange = (employerInfo: UpdateEmployerModel) => {
    updateEmployerInfo(employer?.id as string, employerInfo)
    setEmployerEditModalVisible(false)
  }

  const handlePayrollSystemConfigurationChange = (payrollSystemConfiguration: PayrollSystemConfigurationModel) => {
    updatePayrollSystemConfiguration(employer?.id as string, payrollSystemConfiguration)
    setPayrollSystemConfigurationEditModalVisible(false)
  }

  const handleSilenceCreditSpaceWarning = (days: number) => {
    setSilenceCreditSpaceWarningModalVisible(false)
    silenceCreditSpaceWarning(employer?.id as string, days)
  }

  const handleUploadLogo = (theme: string, file: File) => {
    uploadEmployerLogo(employer?.id as string, theme, file)
    setUploadLogoModalVisible(false)
  }

  const handleDeleteEmployerLogo = (logo: EmployerLogoModel) => {
    setEmployerLogoDeleteCandidate(logo)
    setConfirmDeleteLogoModalVisible(true)
  }

  const handleConfirmDeleteEmployerLogo = () => {
    deleteEmployerLogo(employer?.id as string, employerLogoDeleteCandidate?.theme as string)
    setConfirmDeleteLogoModalVisible(false)
  }

  const renderCompanyInformationCardBody = () => {
    return (
      <div>
        <p>Customer: <Link to={`/customers/${employer?.customerId}`}>{employer?.customerName}</Link></p>
        <p>Abbreviation: <span>{employer?.appAbbreviation}</span></p>
        <p>Employee activation type: <span>{employer?.employeeActivationType}</span></p>
        <p>Employee deactivation type: <span>{employer?.employeeDeactivationType}</span></p>
        <p>Default tax table: <span>{employer?.defaultTaxTableIdentifier}</span></p>
        <p>Default fix income tax: <span>{employer?.defaultFixedTaxRatePercent}%</span></p>
        <p>Default payout tax: <span>{employer?.defaultPayoutTaxInPercent}%</span></p>
        <p>Payout tax immutable: <span>{employer?.payoutTaxImmutable ? "yes" : "no"}</span></p>
        <p>Employee data parser type: <span>{employer?.employeeDataParserType}</span></p>
        <p>Default percentage for vacation pay: <span>{employer?.defaultVacationPayPercent}</span></p>
        <p>Free withdrawal count on activation: <span>{employer?.freePayoutCountOnEmployeeActivation}</span></p>
        <p>Send new employee welcome messages: <span>{employer?.newEmployeeMessageEnabled ? "yes" : "no"}</span></p>
        <p>Send employee activation reminders: <span>{employer?.employeeActivationReminderEnabled ? "yes" : "no"}</span></p>
        <p>Activation reminder interval (days): <span>{employer?.employeeActivationReminderIntervalDays}</span></p>
        <p>Planned shifts enabled: <span>{employer?.plannedShiftsEnabled ? "yes" : "no"}</span></p>
        <p>Open shifts enabled: <span>{employer?.openShiftsEnabled ? "yes" : "no"}</span></p>
        <p>New employees report enabled: <span>{employer?.newEmployeesReportEnabled ? "yes" : "no"}</span></p>
        <p>Employer initiated payouts enabled: <span>{employer?.employerInitiatedPayoutsEnabled ? "yes" : "no"}</span></p>
        <p>Employer initiated payout tax percent: <span>{employer?.employerInitiatedPayoutTaxPercent}</span></p>
        <p>Employer initiated payout config: <span>{employer?.employerInitiatedPayoutClientConfiguration}</span></p>
        <p>Employee min age: <span>{employer?.employeeMinAgeInYears}</span></p>
        <p>Launch date: <span>{employer?.launchDate != null ? formatDateTimeString(employer?.launchDate) : 'Not specified'}</span></p>
        <p>Last data sync: <span>{employer?.lastShiftOrSalaryUpdate != null ? formatDateTimeString(employer.lastShiftOrSalaryUpdate) : 'N/A'}</span></p>
        <p>Stale sync threshold: <span>{employer?.shiftOrDeviationStaleWarningThreshold != null ?  employer?.shiftOrDeviationStaleWarningThreshold : 'N/A'}</span></p>
        <p>Shift sync max delete %: <span>{employer?.shiftSyncMaxDeletePercentage}</span></p>
        <p>WFM system: <span>{employer?.wfmSystem}</span></p>
        <p>HR system: <span>{employer?.hrSystem}</span></p>
        <p>Payroll agency: <span>{employer?.payrollAgency}</span></p>
      </div>
    )
  }

  const renderPayrollSystemConfigurationCardBody = () => {
    return (
      <div>
        <p>Admin flow: <span>{employer?.payrollSystemConfiguration.adminFlowType}</span></p>
        <p>Employee bank account register: <span>{employer?.payrollSystemConfiguration.employeeBankAccountRegisterType}</span></p>
        <p>Payroll system: <span>{employer?.payrollSystemConfiguration.payrollSystem}</span></p>
        <p>Payroll file format: <span>{employer?.payrollSystemConfiguration.payrollFileFormat}</span></p>
        <p>Payroll file employee identification: <span>{employer?.payrollSystemConfiguration.payrollFileEmployeeIdentifierType}</span></p>
        <p>Payroll system import file format: <span>{employer?.payrollSystemConfiguration.payrollSystemImportFileFormat}</span></p>
        <p>Enable payroll file adjustment: <span>{employer?.payrollSystemConfiguration.enablePayrollFileAdjustment ? "yes" : "no"}</span></p>
        <p>Manual payroll file row creation enabled: <span>{employer?.payrollSystemConfiguration.manualPayrollFileRowCreationEnabled ? "yes" : "no"}</span></p>
        <p>Payroll file row deletion enabled: <span>{employer?.payrollSystemConfiguration.payrollFileRowDeletionEnabled ? "yes" : "no"}</span></p>
        <p>Payroll file execution date one day before payment: <span>{employer?.payrollSystemConfiguration.payrollFileExecutionDateOneDayBeforePayment ? "yes" : "no"}</span></p>
        <p>Payroll file handler config: <span>{employer?.payrollSystemConfiguration.payrollFileHandlerConfiguration}</span></p>
        <p>Enable payroll system employee deactivation import file: <span>{employer?.payrollSystemConfiguration.enableEmployeeDeactivationImportFile ? "yes" : "no"}</span></p>
        <p>Enable payroll system bank account import file: <span>{employer?.payrollSystemConfiguration.enableBankAccountImportFile ? "yes" : "no"}</span></p>
        <p>Payroll system bank account import file custom field: <span>{employer?.payrollSystemConfiguration.bankAccountImportFileCustomField}</span></p>
        <p>Payout basis report generation: <span>{employer?.payrollSystemConfiguration.calendarMonthPayoutBasisReportGenerationType}</span></p>
        <p>Day of month for early payout basis report generation: <span>{employer?.payrollSystemConfiguration.calenderMonthPayoutBasisReportGenerationDayOfMonth ?? 'Not set'}</span></p>
        <p>Disable deduction file generation: <span>{employer?.payrollSystemConfiguration.disableDeductionFileGeneration ? "yes" : "no"}</span></p>
        <p>Automatic payroll creation days before payday: <span>{employer?.payrollSystemConfiguration.automaticPayrollCreationDaysBeforePayday != null ? employer.payrollSystemConfiguration.automaticPayrollCreationDaysBeforePayday : 'disabled'}</span></p>
        <p>Disable payroll creation until days before payday: <span>{employer?.payrollSystemConfiguration.payrollCreationDisabledUntilDaysBeforePayday != null ? employer.payrollSystemConfiguration.payrollCreationDisabledUntilDaysBeforePayday : 'disabled'}</span></p>
        <p>Use parallel employment contracts: <span>{employer?.payrollSystemConfiguration.useParallelEmploymentContracts ? "yes" : "no"}</span></p>
      </div>
    )
  }

  const renderBankInformationCardBody = () => {
    return (
      <div>
        <p>Bank account number: <span>{employer?.bankAccount?.bankAccountNumber ?? 'Not specified'}</span></p>
      </div>
    )
  }

  const renderNotificationCardBody = () => {
    return (
      <div>
        <p>Activation notifications: <span>{employer?.activationNotificationDaysOfWeek?.join(', ') ?? 'Not specified'}</span></p>
      </div>
    )
  }

  const renderPayArticleTypeCardBody = () => {
    return (
      <div>
        {!!payArticleTypesError && <Alert variant='danger'>{payArticleTypesError.toString()}</Alert>}
        <PayArticleTypeList
          payArticleTypes={payArticleTypes}
          onEditClick={handleEditPayArticleType}
          onDeleteClick={handleDeletePayArticleType}
        />
      </div>
    )
  }

  const renderCreditCardBody = () => {
    return (
      <div>
        <p>Approved credit: <span>{formatMoney(employer?.approvedCreditLimit)}</span></p>
        <p>Allocated credit: <span>{formatMoney(employer?.availableCreditLimit)}</span></p>
        <p>Current usage: <span>{formatMoney(employer?.creditUsage)}</span></p>
        <p>Remaining credit space warning: <span>{employer?.remainingCreditSpaceWarningPercent}%</span></p>
        <p>Credit space warning silenced until: <span>{formatDateTimeString(employer?.creditSpaceWarningSilencedUntilDate)}</span>
          <FontAwesomeIcon
            onClick={() => setSilenceCreditSpaceWarningModalVisible(true)}
            className={styles.inlineIcon}
            icon={faPen}
            color={Colors.euro_600}
          />
        </p>
      </div>
    )
  }

  const renderPayoutSettingsCardBody = () => {
    return (
      <div>
        <p>Min amount per payout: <span>{employer?.payoutSettings?.minAmountPerPayout != null ? employer?.payoutSettings?.minAmountPerPayout : `${employer?.effectivePayoutSettings?.minAmountPerPayout} ${inherited}`}</span></p>
        <p>Max amount per pay period: <span>{employer?.payoutSettings?.maxAmountPerPayday != null ? employer?.payoutSettings?.maxAmountPerPayday : `${employer?.effectivePayoutSettings?.maxAmountPerPayday} ${inherited}`}</span></p>
        <p>Max % per pay period: <span>{employer?.payoutSettings?.maxPercentPerPayday != null ? employer?.payoutSettings?.maxPercentPerPayday : `${employer?.effectivePayoutSettings?.maxPercentPerPayday} ${inherited}`}</span></p>
        {/* <p>Max amount per payout: <span>{employer?.payoutSettings?.maxAmountPerPayout || notSpecified}</span></p> */}
        <p>Max # of payouts per pay period: <span>{employer?.payoutSettings?.maxNumberOfPayoutsPerPayday != null ? employer?.payoutSettings?.maxNumberOfPayoutsPerPayday : `${employer?.effectivePayoutSettings?.maxNumberOfPayoutsPerPayday ?? '∞'} ${inherited}`}</span></p>
        <p>Fee: <span>{employer?.payoutSettings?.fee != null ? employer?.payoutSettings?.fee : `${employer?.effectivePayoutSettings?.fee} ${inherited}`}</span></p>
        <p>Currency: <span>{employer?.payoutSettings?.currency != null ? employer?.payoutSettings?.currency?.toUpperCase() : `${employer?.effectivePayoutSettings?.currency?.toUpperCase()} ${inherited}`}</span></p>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <h1>Employer: {employer?.customerName}</h1>
        <StateBadge state={employer?.state} />
      </div>
      <div className={styles.actionBar}>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-start'}}>
          <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)} />
        </div>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-end'}}>
        </div>
      </div>
      <CardSection>
        <DetailsCard onClick={() => setEmployerEditModalVisible(true)}>
          <h6>General information</h6>
          {renderCompanyInformationCardBody()}
        </DetailsCard>
        <DetailsCard onClick={() => setPayrollSystemConfigurationEditModalVisible(true)}>
          <h6>Payroll</h6>
          {renderPayrollSystemConfigurationCardBody()}
        </DetailsCard>
        <DetailsCard onClick={() => setPayoutSettingsEditModalVisible(true)}>
          <h6>Payout settings</h6>
          {renderPayoutSettingsCardBody()}
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard iconType='Add' onClick={() => setAddPayrollCodeModalVisible(true)}>
          <h6>Payroll codes</h6>
          <PayrollCodeList
            payrollCodes={employer?.payrollSystemConfiguration.payrollCodes ?? []}
            onDeleteClick={handleDeletePayrollCode}
        />
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard iconType='Add' onClick={handleAddPayArticleTypeClick}>
          <h6>Pay article types</h6>
          {renderPayArticleTypeCardBody()}
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard onClick={() => setEmployerEditModalVisible(true)}>
            <h6>Credit information</h6>
            {renderCreditCardBody()}
          </DetailsCard>
        <DetailsCard iconType='Add' onClick={() => setAddEmployerCreditorModalVisible(true)}>
            <h6>Creditors</h6>
            <CreditorConnectionList
              creditors={employer?.creditors ?? []}
              onDeleteClick={handleDeleteEmployerCreditor}
            />
        </DetailsCard>
        <DetailsCard onClick={!employer?.bankAccount?.id ? () => setBankAccountSelectorModalVisible(true) : undefined}>
          <h6>Bank information</h6>
          {renderBankInformationCardBody()}
        </DetailsCard>
        <DetailsCard>
          <h6>Notifications</h6>
          {renderNotificationCardBody()}
        </DetailsCard>
        <DetailsCard onClick={() => setUploadLogoModalVisible(true)}>
            <h6>Logos</h6>
            <EmployerLogoList
              employerLogos={employer?.employerLogos ?? []}
              onDeleteClick={handleDeleteEmployerLogo}
            />
          </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard iconType='Add' onClick={() => navigate(`/employers/${params.id}/payschedules/create`)}>
            <h6>Pay schedules</h6>
            {employer && <PayScheduleList
              paySchedules={employer.paySchedules}
              employerId={employer.id}
            />}
        </DetailsCard>
      </CardSection>
      {employer?.bookkeepingConfiguration &&
          <CardSection>
              <DetailsCard>
                  <h6>Bookkeeping settings</h6>
                  <EmployerBookkeepingConfiguration bookkeepingConfiguration={employer.bookkeepingConfiguration}/>
              </DetailsCard>
          </CardSection>
      }
      <CardSection>
        <DetailsCard >
          <h6>Employees</h6>
          {employer && <EmployeeList employerId={employer.id} />}
        </DetailsCard>
      </CardSection>
      <Divider height={50}/>
      <ConfirmModal
        visible={confirmSetBankAccountModalVisible}
        confirmText={`Are you sure you want to connect this bank account? This cannot be changed.`}
        onConfirmClick={handleSetBankAccountConfirmed}
        onCancelClick={() => setConfirmSetBankAccountModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmDeleteEmployerCreditorModalVisible}
        confirmText={`Are you sure you want to remove ${employerCreditorDeleteCandidate?.name} as a creditor?`}
        onConfirmClick={handleConfirmDeleteEmployerCreditor}
        onCancelClick={() => setConfirmDeleteEmployerCreditorModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmDeletePayrollCodeModalVisible}
        confirmText={`Are you sure you want to remove this payroll code?`}
        onConfirmClick={handleConfirmDeletePayrollCode}
        onCancelClick={() => setConfirmDeletePayrollCodeModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmDeletePayArticleTypeModalVisible}
        confirmText={`Are you sure you want to remove pay article type ${payArticleTypeDeleteCandidate?.code}?`}
        onConfirmClick={handleConfirmDeletePayArticleType}
        onCancelClick={() => setConfirmDeletePayArticleTypeModalVisible(false)}
      />
      <AddCreditorConnectionModal
        visible={addEmployerCreditorModalVisible}
        marketId={employer?.marketId ?? ''}
        existingCreditorConnectionModels={employer?.creditors ?? []}
        onAddClick={handleAddEmployerCreditor}
        onCancelClick={() => setAddEmployerCreditorModalVisible(false)}
      />
      <BankAccountSelectorModal
        marketId={employer?.marketId ?? ''}
        visible={bankAccountSelectorModalVisible}
        onSelectClick={handleBankAccountSelected}
        onCancelClick={() => setBankAccountSelectorModalVisible(false)}
      />
      <EditPayoutSettingsModal
        visible={payoutSettingsEditModalVisible}
        currencyEditable={false}
        initialValue={employer?.payoutSettings}
        marketId={employer?.marketId ?? ''}
        onCancelClick={() => setPayoutSettingsEditModalVisible(false)}
        onSaveClick= {handlePayoutSettingsChange}
      />
      <EditEmployerInfoModal
        visible={employerEditModalVisible}
        initialValue={employer ?? undefined}
        onCancelClick={() => setEmployerEditModalVisible(false)}
        onSaveClick={handleEmployerInfoChange}
      />
      <EditPayrollSystemConfigurationModal
        visible={payrollSystemConfigurationEditModalVisible}
        initialValue={employer?.payrollSystemConfiguration ?? undefined}
        onCancelClick={() => setPayrollSystemConfigurationEditModalVisible(false)}
        onSaveClick={handlePayrollSystemConfigurationChange}
      />
      <AddPayrollCodeModal
        visible={addPayrollCodeModalVisible}
        onAddClick={handleAddPayrollCode}
        onCancelClick={() => setAddPayrollCodeModalVisible(false)}
      />
      <EditPayArticleTypeModal
        visible={payArticleTypeModalVisible}
        initialValue={payArticleTypeEditCandidate ?? undefined}
        error={payArticleTypeModalError}
        onSaveClick={handleSavePayArticleType}
        onCancelClick={handlePayArticleTypeModalClose}
      />
      <UploadLogoModal
        visible={uploadLogoModalVisible}
        onUpload={(theme, file) => {handleUploadLogo(theme, file)}}
        onCancelClick={() => setUploadLogoModalVisible(false)}/>
      <ConfirmModal
        visible={confirmDeleteLogoModalVisible}
        confirmText={`Are you sure you want to remove the ${employerLogoDeleteCandidate?.theme} logo?`}
        onConfirmClick={handleConfirmDeleteEmployerLogo}
        onCancelClick={() => setConfirmDeleteLogoModalVisible(false)}
      />
      <SilenceCreditSpaceWarningModal
        visible={silenceCreditSpaceWarningModalVisible}
        onCancelClick={() => setSilenceCreditSpaceWarningModalVisible(false)}
        onSaveClick={handleSilenceCreditSpaceWarning}
      />
      <FullScreenLoadingIndicator visible={loading || payArticleTypesLoading} />
    </div>
  )
}

export default EmployerDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    },
    '& h5': {
      fontFamily: 'Aestetico-Regular',
      color: Colors.darkDefault,
    }
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 10,
    alignItems: 'center',
    marginBottom: titleMarginBottom,
    '& h1': {
      marginBottom: 0
    }
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  actionBarCell: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 15,
    alignItems: 'center',
    width: '33%'
  },
  navLinkContainer: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 10
  },
  cardColumnContainer: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 10
  },
  largeButton: {
    fontSize: "xxx-large",
    alignSelf: "center",
    margin: "20px",
    flexGrow: 1,
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro5,
      cursor: 'pointer',
    }
  },
  inlineIcon: {
    marginLeft: 5,
    '&:hover': {
      color: Colors.euro5,
      cursor: 'pointer',
    }
  }
})
